import { html, TemplateResult } from "lit-html";
import {
  appendRequiredAsterisk,
  getInfoBox,
  getLabel,
} from "Components/abstract/sc-e-form-field/sc-e-form-field.template";
import { HIDDEN } from "Constants/cssClasses";
import { ifDefined } from "lit/directives/if-defined.js";

import { GetInputArgs, SelectComponentRenderingArgs, SelectTemplateArgs, ScSelectOption } from "./sc-e-select.types";
import { ScSelect } from "./sc-e-select";

/**
 * Return TemplateResult of formfield input
 * @param {GetInputArgs} args
 * @returns {TemplateResult}
 */
export const getInput = (args: GetInputArgs): TemplateResult => {
  return html`
    <input
      type="text"
      placeholder="${appendRequiredAsterisk(args.placeholder, args.isRequired)}"
      class="sc-e-form-field__input sc-js-form-field__input sc-e-select__input"
      aria-describedby="info-box-${args.id}"
      tabindex=${ifDefined(args.isReadonly ? "-1" : undefined)}
      id="${args.id}"
      ?required=${args.isRequired}
      readonly
      ?disabled=${args.isDisabled}
      @keyup=${args.keyupCb}
    />
  `;
};

const isOptionHidden = (option: ScSelectOption, selectComponent: ScSelect) => {
  if (selectComponent.optionFilter.length === 0) return false;
  return selectComponent.optionFilter.findIndex(allowedOption => allowedOption === option.value) === -1;
};

const renderOption = (option: ScSelectOption, selectComponent: ScSelect, index: number) => {
  const isoptionSelected = selectComponent.selectedOption?.value === option.value;
  return html`
    <span
      class="sc-e-select__option ${isoptionSelected ? "sc-state-active" : ""} ${isOptionHidden(option, selectComponent)
        ? HIDDEN
        : ""}"
      role="option"
      aria-selected="${isoptionSelected}"
      id="${index}_${selectComponent.id}"
      @click=${() => selectComponent.onOptionSelect(option.value)}
    >
      ${option.label}</span
    >
  `;
};

export const selectTemplate = (args: SelectTemplateArgs) => {
  return html`
    <div class="sc-e-form-field__box">
      ${args.label !== undefined
        ? getLabel({ for: args.id, id: args.labeId, label: args.label, isRequired: args.isRequired })
        : ""}
      <select class="sc-e-select__input-select" name="${args.name}--native" aria-hidden="true">
        <option
          value="${args.selectComponent.selectedOption?.value === undefined
            ? " "
            : args.selectComponent.selectedOption?.value}"
          selected
        ></option>
      </select>
      <div
        class="sc-e-select__wrapper"
        aria-controls="listbox_${args.id}"
        aria-expanded="false"
        aria-haspopup="listbox"
        aria-labelledby="label_${args.id}"
        id="combo_${args.id}"
        role="combobox"
        tabindex="0"
      >
        ${getInput({
          ...args,
          label: args.placeholder,
          isReadonly: true,
        })}
        <sc-e-icon
          class="sc-e-icon sc-e-select__toggle"
          icon-id="sc-icon-keyboard-arrow-down"
          aria-hidden="true"
        ></sc-e-icon>
      </div>
      <div
        class="sc-e-select__flyout"
        role="listbox"
        id="listbox_${args.id}"
        aria-labelledby="label_${args.id}"
        tabindex="-1"
      >
        ${args.options.map((option, index) => renderOption(option, args.selectComponent, index))}
      </div>
    </div>
    ${getInfoBox({
      formField: args.selectComponent,
    })}
  `;
};

export const renderScSelect = (args: SelectComponentRenderingArgs) => {
  return html`
    <sc-e-select
      class="sc-e-select sc-e-form-field ${args.cssClasses || ""}"
      label="${args.label}"
      placeholder="${args.placeholder}"
    >
      ${args.options.map(option => {
        return html`<option class="sc-e-select__option" ?selected=${option.selected} value="${option.value}">
          ${option.label}
        </option>`;
      })}
    </sc-e-select>
  `;
};
